import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Tag, Form, Row, Col } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ImportChannel, Provider } from '@/api/generated/schemas';
import type { ContentProviderImportOut } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useAdminTable } from '@/hooks/useAdminTable';
import { ActiveFilters } from '@/components/common/ActiveFilters';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { SelectFilter } from '@/components/common/SelectFilter';
import { ReimportButton } from '@/components/content_catalog/provider_imports/ReimportButton';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const ProviderImportList = () => {
  const t = useTranslate();
  const { onSearch, createDebouncedSubmit } = useTableSearch();
  const f = (key: string) =>
    t(`content_catalog.provider_imports.fields.${key}`);

  const { tableProps, searchFormProps, sorters, filters, setFilters } =
    useAdminTable<ContentProviderImportOut>({
      pagination: { pageSize: 20 },
      resource: 'content_catalog.provider_imports',
      onSearch,
      sorters: {
        initial: [{ field: 'created_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);
  const crudFilters = filters as CrudFilter[];

  const formatFilterValue = (
    field: string,
    value: string | number | boolean
  ) => {
    if (field === 'provider' || field === 'channel')
      return t(`content_catalog.provider_imports.${field}.${value}`);
    return undefined;
  };

  return (
    <List canCreate={false}>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle">
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t(
                  'content_catalog.provider_imports.search.placeholder'
                )}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <ActiveFilters
        filters={crudFilters}
        setFilters={setFilters}
        labelPrefix="content_catalog.provider_imports"
        ignore={['q']}
        formatValue={formatFilterValue}
      />

      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          ...(typeof tableProps.pagination === 'object'
            ? tableProps.pagination
            : {}),
          showTotal: (total) => t('base.total', { total }),
        }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="name"
          title={f('name')}
          render={(value: string | null) => value || '-'}
        />

        <Table.Column
          dataIndex="provider"
          title={f('provider')}
          sorter
          sortOrder={getSortOrder(sorters, 'provider')}
          render={(value: Provider) => (
            <Tag>
              {t(`content_catalog.provider_imports.provider.${value}`, {
                defaultValue: value,
              })}
            </Tag>
          )}
          filtered={isFieldFiltered(crudFilters, 'provider')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="provider"
              placeholder={f('provider')}
              options={Object.values(Provider).map((v) => ({
                label: t(`content_catalog.provider_imports.provider.${v}`),
                value: v,
              }))}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="channel"
          title={f('channel')}
          sorter
          sortOrder={getSortOrder(sorters, 'channel')}
          render={(value: ImportChannel) => (
            <Tag>
              {t(`content_catalog.provider_imports.channel.${value}`, {
                defaultValue: value,
              })}
            </Tag>
          )}
          filtered={isFieldFiltered(crudFilters, 'channel')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="channel"
              placeholder={f('channel')}
              options={Object.values(ImportChannel).map((v) => ({
                label: t(`content_catalog.provider_imports.channel.${v}`),
                value: v,
              }))}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="s3_object_reference_id"
          title={f('s3_object_reference_id')}
          render={(value: number | null) => value ?? '-'}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          render={(value: string) => new Date(value).toLocaleString()}
          filtered={isFieldFiltered(crudFilters, 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          title={t('base.fields.actions')}
          fixed="right"
          render={(_, record: ContentProviderImportOut) => (
            <ReimportButton providerImport={record} />
          )}
        />
      </Table>
    </List>
  );
};
